import { useState, useCallback, useEffect, useMemo } from "react";
import { Option } from "@campgladiator/cgui-core.atoms.dropdown";
import type { CouponDTO, Role } from "@campgladiator/cg-common.types.types";
import { fetchCoupons } from "@campgladiator/cg-common.api.coupons";
import { parseCoupons } from "@campgladiator/cg-common.logic.memberships";
import { excludedStripeCouponCodeIds } from "@campgladiator/cg-common.logic.coupons";

export type UseSelectCouponInputProps = {
  role: Role;
  selectedProductId: string;
};

export type SelectedCouponOption = {
  amountOff?: CouponDTO["amountOff"];
  applicableProductIds: CouponDTO["applicableProductIds"];
  couponCode: CouponDTO["stripeCouponName"];
  duration: CouponDTO["duration"];
  id: CouponDTO["stripeCouponId"];
  percentageDiscount: CouponDTO["percentageDiscount"];
};

const useSelectCouponInput = ({
  selectedProductId,
  role,
}: UseSelectCouponInputProps) => {
  const [selectedCouponOption, setSelectedCouponOption] =
    useState<SelectedCouponOption | null>(null);
  const [couponOptions, setCouponOptions] = useState<Option[]>([]);

  const getSelectedCouponOption = (option?: Option) =>
    option
      ? {
          id: option.itemId,
          applicableProductIds: option.metadata?.applicableProductIds,
          couponCode: option.metadata?.stripeCouponName,
          duration: option.metadata?.duration,
          percentageDiscount: option.metadata?.percentageDiscount,
          amountOff: option.metadata?.amountOff,
        }
      : null;

  const handleOnSelectCoupon = (option?: Option) =>
    setSelectedCouponOption(getSelectedCouponOption(option));

  const coupons: Promise<CouponDTO[]> = useMemo(async () => {
    try {
      return await fetchCoupons();
    } catch (error) {
      return [];
    }
  }, [fetchCoupons]);

  const loadCouponsOptions = useCallback(async () => {
    try {
      const couponOptions = await coupons;
      const couponsAllowed = couponOptions?.filter(
        ({ type, applicableProductIds, valid, stripeCouponId }) =>
          (type === "PERCENTAGE" || type === "FIXED") &&
          applicableProductIds?.includes(selectedProductId) &&
          valid &&
          !(
            role === "TRAINER" &&
            excludedStripeCouponCodeIds.includes(stripeCouponId)
          )
      );
      const dropdownOptions = parseCoupons(couponsAllowed);
      setCouponOptions(dropdownOptions);
      setSelectedCouponOption(null);
    } catch (error) {
      console.error("Failed to load coupons:", error);
    }
  }, [selectedProductId]);

  useEffect(() => {
    loadCouponsOptions();
  }, [loadCouponsOptions]);

  return {
    couponOptions,
    handleOnSelectCoupon,
    selectedCouponOption,
  };
};

export default useSelectCouponInput;
